import { toast } from "react-toastify";
import Survey from "components/Survey/Survey";
import appApi from "api";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import Cookies from "js-cookie";
import { SURVEY_RESULTS_COOKIE_NAME, EMBEDDED_SURVEY_RESULTS_COOKIE_NAME } from "@fhc/shared/constants";
import { parseJSON } from "@fhc/shared";
import SurveyNotFound from "components/Survey/SurveyNotFound";
import { useSearchParams } from "react-router-dom";
import { removeAnchorLink } from "utils";

const FunctionalSurvey = ({ isIframe }) => {
    const location = useLocation();
    const { surveyLinkKey } = useParams();

    const navigate = useNavigate();

    const [theme, setTheme] = useState(null);
    const [surveyInfo, setSurveyInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [surveyNotFound, setSurveyNotFound] = useState(false);
    const [isUrlAllowed, setIsUrlAllowed] = useState(false); // iframe state
    const [searchParams, setSearchParams] = useSearchParams();
    const [name, setName] = useState(null);

    useEffect(() => {
        if (theme === null) return;
        document.title = theme.appName;
    }, [theme]);

    //todo find a way to not duplicate this function, it's used here and the SurveyAuthentication component
    //todo a custom hook was tried, but it didn't work when used with useNavigate() in the same component
    const navigateToResultsPage = useCallback(
        (resultUuid) => {
            navigate({
                pathname: `/survey/results/${surveyLinkKey}`,
                search: `?uuid=${resultUuid}`,
            });
        },
        [surveyLinkKey, navigate],
    );

    const handleFinishSurvey = async (responses, captchaToken) => {
        try {
            // Get bulk upload link respondent ID if applicable
            const respondent_id = searchParams.get("respondent_id");
            const surveyInfo = await getSurveyInfo();
            const response = await appApi.post("/surveys/results", {
                company_id: surveyInfo.company.id,
                responses,
                questions: surveyInfo.company.questions,
                is_iframe: isIframe,
                key: surveyLinkKey,
                captchaToken,
                respondent_id,
                advisor_direct_link: surveyInfo.advisor_direct_link,
                origin: removeAnchorLink(window.location.origin),
                respondent_name: name,
            });

            navigateToResultsPage(response.data.results.uuid);
        } catch (error) {
            if (error.response?.status === 401) {
                toast.error("reCAPTCHA could not be verified, please try again");
            } else if (error.response?.status === 409 || error.response?.status === 410) {
                // Treat deactivated/closed links and deleted links the same because it will just add confusion from a user prespective
                toast.error(
                    "We're sorry, the survey was closed while you were responding and is no longer accepting responses",
                );
            } else {
                // Treating 404 survey link not found as an internal error because it most likely is.
                // Otherwise, 404 should only occur if the link in the was manually deleted from the DB
                toast.error(`${theme.metricName} score could not be calculated due to an internal error`);
            }
        }
    };

    // Gets the company's survey questions, theme, and a boolean that indicates if the survey is active
    // (using a generic name for this function because the backend route is not cohesive)
    const getSurveyInfo = async () => {
        try {
            const response = await appApi.get("/company/external/surveys", {
                params: {
                    link_key: surveyLinkKey,
                },
            });

            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                setSurveyNotFound(true);
            } else {
                toast.error("Internal error");
            }
        }
    };

    useEffect(() => {
        const validateHostUrl = async (hostUrl) => {
            try {
                // Returns 200 if the URL is allowed, so we don't need to check the response
                await appApi.get("/company/uri/allowed", {
                    params: {
                        url: hostUrl,
                        link_key: surveyLinkKey,
                    },
                });

                return true;
            } catch (error) {
                // 404 is returned if the host url is not found in the whitelist list
                if (error.response?.status === 404) {
                    return false;
                }

                throw error;
            }
        };

        const fetchDataAndResultId = async () => {
            try {
                setLoading(true);

                if (isIframe) {
                    // Make sure the host URL is whitelisted if the survey is being loaded in an iframe
                    const hostUrl = window.location.ancestorOrigins[0];
                    setIsUrlAllowed(await validateHostUrl(hostUrl));
                }

                // Redirect to the results page if the user has already responded to this survey instance
                if (!location.pathname.startsWith("/survey/results")) {
                    //!IMPORTANT: This causes an extra render. However, it doesn't seem to be degrading the UX. Just leaving this note here in case it becomes a problem.
                    const surveyResultsCookie = parseJSON(
                        Cookies.get(isIframe ? EMBEDDED_SURVEY_RESULTS_COOKIE_NAME : SURVEY_RESULTS_COOKIE_NAME),
                    );

                    // Redirect to the results page if there's a result cookie with the same link key as the URL
                    if (surveyResultsCookie && surveyResultsCookie.link_key === surveyLinkKey) {
                        navigateToResultsPage(surveyResultsCookie.uuid);
                    }
                }

                const surveyInfo = await getSurveyInfo();

                if (surveyInfo) {
                    setSurveyInfo(surveyInfo);
                    if (Object.keys(surveyInfo.company.theme).length > 0)
                        setTheme((theme) => ({ ...theme, ...surveyInfo.company.theme }));
                    if (!surveyInfo.is_link_active) {
                        // Checking for the survey result ID (it comes from the URL) so the toast isn't displayed on the results page
                        toast.error("Survey has been closed and is no longer accepting response");
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchDataAndResultId();
    }, [surveyLinkKey, isIframe, navigate, navigateToResultsPage]);

    return surveyNotFound ? (
        <SurveyNotFound />
    ) : (
        <div
            style={{
                position: "fixed",
                height: "100%",
                width: "100%",
                overflow: "auto",
            }}
        >
            {!loading && (
                <>
                    {isIframe && !isUrlAllowed ? (
                        <Box>URL is not allowed</Box>
                    ) : (
                        <Survey
                            themeColors={theme}
                            companyDetails={surveyInfo.company}
                            advisorDirectLink={surveyInfo.advisor_direct_link}
                            onFinish={handleFinishSurvey}
                            disableStartButton={!surveyInfo.is_link_active}
                            name={name}
                            setName={setName}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default FunctionalSurvey;
